import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useHelpFlyout } from '../../context/helpFlyoutContext';
import { PROVIDER_REQUIREMENTS_HELP_CONTENT_ID } from '../../util/userTypeRedirects';
import { countIncompleteProviderRequirements } from '../../util/providerRequirementsCheck';
import NotificationBadge from '../NotificationBadge/NotificationBadge';

import css from './HelpFlyoutFloatingButton.module.css';

/**
 * Floating tab on the right edge of the screen that opens the help flyout.
 * Visible only to logged-in provider-main users while the flyout is closed;
 * the open-state counterpart lives on the HelpFlyout panel edge.
 * Shows an inbox-style bubble with the number of unmet requirements while
 * the completion flag is not true (mirrors the Requirements tab visibility).
 */
const HelpFlyoutFloatingButton = () => {
  const intl = useIntl();
  const { isOpen, openHelp } = useHelpFlyout();
  const currentUser = useSelector(state => state.user?.currentUser);
  const currentUserHasListings = useSelector(state => state.user?.currentUserHasListings);
  const stripeAccount = useSelector(state => state.stripeConnectAccount?.stripeAccount);

  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  if (userType !== 'provider-main' || isOpen) {
    return null;
  }

  const requirementsComplete =
    currentUser?.attributes?.profile?.publicData?.providerRequirementsComplete === true;
  const incompleteCount = requirementsComplete
    ? 0
    : countIncompleteProviderRequirements(currentUser, {
        hasListings: currentUserHasListings,
        stripeAccount,
      });
  const showBadge = incompleteCount > 0;

  const label = showBadge
    ? intl.formatMessage(
        { id: 'HelpFlyout.floatingButtonLabelIncomplete' },
        { count: incompleteCount }
      )
    : intl.formatMessage({ id: 'HelpFlyout.floatingButtonLabel' });

  return (
    <button
      type="button"
      className={css.root}
      onClick={() => openHelp(PROVIDER_REQUIREMENTS_HELP_CONTENT_ID)}
      aria-expanded={false}
      aria-label={label}
      title={label}
      data-testid="HelpFlyoutFloatingButton"
    >
      {showBadge ? (
        <span
          className={css.badge}
          aria-hidden="true"
          data-testid="HelpFlyoutFloatingButton.badge"
        >
          <NotificationBadge count={incompleteCount} />
        </span>
      ) : null}
      <span className={css.label} aria-hidden="true">
        <FormattedMessage id="HelpFlyout.floatingButtonLabel" />
      </span>
      <span className={css.icon} aria-hidden="true">
        ?
      </span>
    </button>
  );
};

export default HelpFlyoutFloatingButton;
